import React, { useState } from "react";
import { LuX, LuImage } from "react-icons/lu";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../utils/firebase";
import { getAuth } from "firebase/auth";

const fieldCls = "w-full border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-ring transition-colors bg-card";
const labelCls = "block text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-wider mb-1.5";

function EditProductModal({ product, onClose, onUpdate }) {
  const [name,          setName]          = useState(product.name        || "");
  const [price,         setPrice]         = useState(product.price       ?? "");
  const [quantity,      setQuantity]      = useState(product.quantity     ?? "");
  const [category,      setCategory]      = useState(product.category    || "");
  const [description,   setDescription]   = useState(product.description || "");
  const [variantsCsv,   setVariantsCsv]   = useState(
    Array.isArray(product.variants) ? product.variants.join(", ") : ""
  );
  const [existingImages, setExistingImages] = useState(
    Array.isArray(product.imageUrls) ? product.imageUrls : []
  );
  const [removedUrls,   setRemovedUrls]   = useState([]);
  const [newImages,     setNewImages]     = useState([]);
  const [heroActive,    setHeroActive]    = useState(!!product.heroActive);
  const [hidden,        setHidden]        = useState(!!product.hidden);
  const [heroImage,     setHeroImage]     = useState(null);
  const [heroPreview,   setHeroPreview]   = useState(null);
  const [isUploading,   setIsUploading]   = useState(false);

  const handleHeroChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    setHeroImage(file);
    setHeroPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleRemoveExisting = (url) => {
    setExistingImages((prev) => prev.filter((u) => u !== url));
    setRemovedUrls((prev) => [...prev, url]);
  };

  const handleSave = async () => {
    setIsUploading(true);
    let imageUrls    = [...existingImages];
    let heroImageUrl = product.heroImageUrl || null;

    try {
      // Delete removed gallery images from Storage
      await Promise.all(
        removedUrls.map((url) =>
          deleteObject(ref(storage, url)).catch(() => { /* already gone */ })
        )
      );

      // Upload new gallery images
      if (newImages.length) {
        const uploads = newImages.map(async (file, idx) => {
          const storageRef = ref(storage, `productImages/${product.id}/${Date.now()}_${idx}_${file.name}`);
          await uploadBytes(storageRef, file, { contentType: file.type || "application/octet-stream" });
          return await getDownloadURL(storageRef);
        });
        imageUrls = [...imageUrls, ...(await Promise.all(uploads))];
      }

      // Replace hero image
      if (heroImage) {
        if (product.heroImageUrl) {
          try { await deleteObject(ref(storage, product.heroImageUrl)); } catch { /* already gone */ }
        }
        const heroRef = ref(storage, `heroBanners/${product.id}/${Date.now()}_${heroImage.name}`);
        await getAuth().currentUser.getIdToken(true);
        await uploadBytes(heroRef, heroImage, { contentType: heroImage.type || "image/jpeg" });
        heroImageUrl = await getDownloadURL(heroRef);
      }

      const variants = variantsCsv.split(",").map((v) => v.trim()).filter(Boolean);

      await updateDoc(doc(db, "products", product.id), {
        name, price: parseFloat(price), quantity: parseInt(quantity, 10),
        category, description, variants, imageUrls, heroActive, heroImageUrl, hidden,
      });

      sessionStorage.removeItem("allProducts");
      onUpdate?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      alert("Save failed. See console for details.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-5 border-b border-border">
          <div>
            <h2 className="text-lg font-black text-foreground">Edit Product</h2>
            <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5 font-mono truncate max-w-[280px]">{product.id}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors text-[var(--color-muted-foreground)]">
            <LuX size={16} />
          </button>
        </div>

        <div className="px-7 py-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Product Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} className={fieldCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Price ($)</label>
              <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} step="0.01" min="0" className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>Quantity</label>
              <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} min="0" className={fieldCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${fieldCls} resize-none`} />
          </div>

          <div>
            <label className={labelCls}>Variants (comma separated)</label>
            <input type="text" value={variantsCsv} onChange={(e) => setVariantsCsv(e.target.value)} placeholder="Blue Razz, Mango Ice, Strawberry" className={fieldCls} />
          </div>

          {/* Existing gallery images */}
          {existingImages.length > 0 && (
            <div>
              <label className={labelCls}>Current Images</label>
              <div className="grid grid-cols-4 gap-2">
                {existingImages.map((url) => (
                  <div key={url} className="relative group">
                    <img src={url} alt="Product" className="w-full aspect-square object-cover rounded-lg border border-border" />
                    <button
                      type="button"
                      onClick={() => handleRemoveExisting(url)}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                    >
                      <LuX size={12} />
                    </button>
                  </div>
                ))}
              </div>
              {removedUrls.length > 0 && (
                <p className="text-xs text-red-400 mt-1.5">{removedUrls.length} image(s) will be deleted on save.</p>
              )}
            </div>
          )}

          {/* Add new gallery images */}
          <div>
            <label className={labelCls}>Add More Images</label>
            <div className="border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-accent transition-colors">
              <input type="file" accept="image/*" multiple onChange={(e) => setNewImages(Array.from(e.target.files || []))} className="hidden" id="edit-gallery" />
              <label htmlFor="edit-gallery" className="cursor-pointer">
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  {newImages.length > 0 ? `${newImages.length} new file(s) selected` : "Click to upload new images"}
                </p>
              </label>
            </div>
          </div>

          {/* Visibility toggle */}
          <div className={`rounded-xl p-4 border ${hidden ? "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/50" : "bg-muted border-border"}`}>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hidden}
                onChange={(e) => setHidden(e.target.checked)}
                className="w-4 h-4 accent-brand-navy"
              />
              <div>
                <span className="text-sm font-semibold text-foreground">Hide this product from the store</span>
                <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">Customers won't see it until you make it visible again.</p>
              </div>
            </label>
          </div>

          {/* Hero banner */}
          <div className="bg-accent/5 dark:bg-accent/10 rounded-xl p-4 space-y-3 border border-accent/10">
            <div>
              <label className={labelCls}>Hero Banner (Homepage Carousel)</label>
              <p className="text-xs text-[var(--color-muted-foreground)] mb-2">Wide landscape image shown in the homepage hero. Recommended: 1600×900px or wider.</p>
              <div className="border-2 border-dashed border-accent/20 rounded-xl p-3 text-center hover:border-accent transition-colors cursor-pointer">
                <input type="file" accept="image/*" onChange={handleHeroChange} className="hidden" id="edit-hero" />
                <label htmlFor="edit-hero" className="cursor-pointer">
                  {heroPreview ? (
                    <img src={heroPreview} alt="Hero preview" className="w-full h-28 object-cover rounded-lg" />
                  ) : product.heroImageUrl ? (
                    <img src={product.heroImageUrl} alt="Current hero" className="w-full h-28 object-cover rounded-lg opacity-70" />
                  ) : (
                    <>
                      <LuImage size={28} className="text-accent opacity-30 mx-auto mb-1" />
                      <p className="text-sm text-[var(--color-muted-foreground)]">Upload hero banner (wide)</p>
                    </>
                  )}
                </label>
              </div>
              <p className="text-xs text-[var(--color-muted-foreground)] mt-1.5">
                {heroPreview ? (
                  <button type="button" onClick={() => { setHeroImage(null); setHeroPreview(null); }} className="text-red-400 hover:text-red-600 font-medium">
                    Remove new selection
                  </button>
                ) : product.heroImageUrl ? "Click image to replace" : null}
              </p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={heroActive}
                onChange={(e) => setHeroActive(e.target.checked)}
                className="w-4 h-4 accent-brand-navy"
              />
              <span className="text-sm font-semibold text-foreground">Feature in homepage hero carousel</span>
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} disabled={isUploading} className="flex-1 border border-border text-foreground font-bold py-3 rounded-xl hover:bg-muted transition-colors text-sm">
              Cancel
            </button>
            <button onClick={handleSave} disabled={isUploading} className="flex-1 bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold py-3 rounded-xl text-sm disabled:opacity-60">
              {isUploading ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditProductModal;
